<template>
    <LayoutAutenticado>
        
        <div class="dashboard-v2 fade-in-up">
            <!-- Header Module -->
            <header class="page-header">
                <div>
                    <h1 class="page-title">Dashboard Corporativo</h1>
                    <p class="page-subtitle">Acompanhe as métricas de performance e balanço consolidado da operação.</p>
                </div>
            </header>

            <!-- Fallback para restrição de features -->
            <div v-if="!hasAnyFeature" class="no-graphs">
                <div class="empty-icon"><i class="bi bi-slash-circle"></i></div>
                <h2>Métricas Restritas</h2>
                <p>
                    O seu plano atual não possui privilégios de visualização gráfica ativos.<br>
                    Contate o <strong>administrador</strong> para solicitar o desbloqueio.
                </p>
            </div>

            <!-- Dash Grid System -->
            <div v-else class="v2-dashboard-grid mt-4">

                <!-- 1. Receivers -->
                <div v-if="hasFeature('GRAPH_RECEIVERS')" class="dashboard-card">
                    <div class="card-header-bar">
                        <div class="card-title-group">
                            <i class="bi bi-graph-up-arrow icon text-success"></i>
                            <h3>Valores a Receber</h3>
                        </div>
                        <div class="card-filters">
                            <div class="select-wrapper default-border minified">
                                <select v-model="filters.receivers.ano" @change="loadDataReceivers()" class="form-select">
                                    <option v-for="a in anosDisponiveis" :key="a" :value="a">{{ a }}</option>
                                </select>
                                <i class="bi bi-chevron-down select-icon"></i>
                            </div>
                        </div>
                    </div>
                    <div class="card-body-section">
                        <div v-if="loadStates.receivers" class="v2-card-loader">
                            <div class="spinner"></div><span>Carregando módulo...</span>
                        </div>
                        <BaseChart v-else-if="chartOptionsReceivers" :option="chartOptionsReceivers" />
                        <div v-else class="chart-empty-state">
                            <i class="bi bi-bar-chart-line empty-state-icon"></i>
                            <p class="empty-state-text">Nenhum dado registrado para o período selecionado</p>
                        </div>
                    </div>
                </div>

                <!-- 2. Pagment -->
                <div v-if="hasFeature('GRAPH_PAGMENT')" class="dashboard-card">
                    <div class="card-header-bar">
                        <div class="card-title-group">
                            <i class="bi bi-graph-down-arrow icon text-danger"></i>
                            <h3>Valores a Pagar</h3>
                        </div>
                        <div class="card-filters">
                            <div class="select-wrapper default-border minified">
                                <select v-model="filters.pagment.ano" @change="loadDataPagment()" class="form-select">
                                    <option v-for="a in anosDisponiveis" :key="a" :value="a">{{ a }}</option>
                                </select>
                                <i class="bi bi-chevron-down select-icon"></i>
                            </div>
                        </div>
                    </div>
                    <div class="card-body-section">
                        <div v-if="loadStates.pagment" class="v2-card-loader">
                            <div class="spinner"></div><span>Carregando módulo...</span>
                        </div>
                        <BaseChart v-else-if="chartOptionsPagment" :option="chartOptionsPagment" />
                        <div v-else class="chart-empty-state">
                            <i class="bi bi-bar-chart-line empty-state-icon"></i>
                            <p class="empty-state-text">Nenhum dado registrado para o período selecionado</p>
                        </div>
                    </div>
                </div>

                <!-- 3. Services Quantities -->
                <div v-if="hasFeature('GRAPH_COMPARE_SERVICE')" class="dashboard-card">
                    <div class="card-header-bar">
                        <div class="card-title-group">
                            <i class="bi bi-boxes icon text-warning"></i>
                            <h3>Serviços Realizados (Geral)</h3>
                        </div>
                        <div class="card-filters">
                            <div class="select-wrapper default-border minified">
                                <select v-model="filters.services.ano" @change="loadDataServices()" class="form-select">
                                    <option v-for="a in anosDisponiveis" :key="a" :value="a">{{ a }}</option>
                                </select>
                                <i class="bi bi-chevron-down select-icon"></i>
                            </div>
                        </div>
                    </div>
                    <div class="card-body-section">
                        <div v-if="loadStates.services" class="v2-card-loader">
                            <div class="spinner"></div><span>Carregando módulo...</span>
                        </div>
                        <BaseChart v-else-if="chartOptionsServices" :option="chartOptionsServices" />
                        <div v-else class="chart-empty-state">
                            <i class="bi bi-bar-chart-line empty-state-icon"></i>
                            <p class="empty-state-text">Nenhum dado registrado para o período selecionado</p>
                        </div>
                    </div>
                </div>

                <!-- 4. Employees -->
                <div v-if="hasFeature('GRAPH_EMPLOYEES')" class="dashboard-card">
                    <div class="card-header-bar">
                        <div class="card-title-group">
                            <i class="bi bi-people-fill icon text-primary"></i>
                            <h3>Performance por Funcionário</h3>
                        </div>
                        <div class="card-filters row-flex gap-2">
                            <div class="select-wrapper default-border minified">
                                <select v-model="filters.employees.ano" @change="loadDataEmployees()" class="form-select">
                                    <option v-for="a in anosDisponiveis" :key="a" :value="a">{{ a }}</option>
                                </select>
                                <i class="bi bi-chevron-down select-icon"></i>
                            </div>
                            <div class="select-wrapper default-border minified">
                                <select v-model="filters.employees.mes" @change="loadDataEmployees()" class="form-select">
                                    <option v-for="(nome, index) in mesesDisponiveis" :key="index" :value="index + 1">{{ nome }}</option>
                                </select>
                                <i class="bi bi-chevron-down select-icon"></i>
                            </div>
                        </div>
                    </div>
                    <div class="card-body-section">
                        <div v-if="loadStates.employees" class="v2-card-loader">
                            <div class="spinner"></div><span>Carregando módulo...</span>
                        </div>
                        <BaseChart v-else-if="chartOptionsEmployee" :option="chartOptionsEmployee" />
                        <div v-else class="chart-empty-state">
                            <i class="bi bi-people empty-state-icon"></i>
                            <p class="empty-state-text">Nenhum funcionário com produção registrada no período</p>
                        </div>
                    </div>
                </div>

                <!-- 5. Base Compares -->
                <div v-if="hasFeature('GRAPH_COMPARE_COMPANYS')" class="dashboard-card">
                    <div class="card-header-bar">
                        <div class="card-title-group">
                            <i class="bi bi-building icon text-primary"></i>
                            <h3>Performance Cruzada (Bases)</h3>
                        </div>
                        <div class="card-filters">
                            <div class="select-wrapper default-border minified">
                                <select v-model="filters.compare.mes" @change="loadDataCompare()" class="form-select">
                                    <option v-for="(nome, index) in mesesDisponiveis" :key="index" :value="index + 1">{{ nome }}</option>
                                </select>
                                <i class="bi bi-chevron-down select-icon"></i>
                            </div>
                        </div>
                    </div>
                    <div class="card-body-section">
                        <div v-if="loadStates.compare" class="v2-card-loader">
                            <div class="spinner"></div><span>Carregando módulo...</span>
                        </div>
                        <BaseChart v-else-if="chartOptionsCompare" :option="chartOptionsCompare" />
                        <div v-else class="chart-empty-state">
                            <i class="bi bi-building empty-state-icon"></i>
                            <p class="empty-state-text">Nenhum dado registrado para o período selecionado</p>
                        </div>
                    </div>
                </div>

                <!-- 6. Service Lookups -->
                <div v-if="hasFeature('GRAPH_CONSULT_SERVICES')" class="dashboard-card">
                    <div class="card-header-bar">
                        <div class="card-title-group">
                            <i class="bi bi-clipboard-data-fill icon text-primary"></i>
                            <h3>Métrica Anual de Categorias</h3>
                        </div>
                        <div class="card-filters row-flex gap-2">
                            <div class="select-wrapper default-border minified">
                                <select v-model="filters.consultaService.ano" @change="loadDataConsultaService()" class="form-select">
                                    <option v-for="a in anosDisponiveis" :key="a" :value="a">{{ a }}</option>
                                </select>
                                <i class="bi bi-chevron-down select-icon"></i>
                            </div>
                            <div class="select-wrapper default-border minified">
                                <select v-model="filters.consultaService.tipoServico" @change="loadDataConsultaService()" class="form-select">
                                    <option v-for="(nome, index) in tiposServicos" :key="index" :value="nome">{{ nome }}</option>
                                </select>
                                <i class="bi bi-chevron-down select-icon"></i>
                            </div>
                        </div>
                    </div>
                    <div class="card-body-section">
                        <div v-if="loadStates.consultaService" class="v2-card-loader">
                            <div class="spinner"></div><span>Carregando módulo...</span>
                        </div>
                        <BaseChart v-else-if="chartOptionsConsultaService" :option="chartOptionsConsultaService" />
                        <div v-else class="chart-empty-state">
                            <i class="bi bi-clipboard-data empty-state-icon"></i>
                            <p class="empty-state-text">Nenhum dado registrado para o período selecionado</p>
                        </div>
                    </div>
                </div>

                <!-- 7. Information Services (Pie) -->
                <div v-if="hasFeature('GRAPH_INFORMATION_SERVICES')" class="dashboard-card">
                    <div class="card-header-bar">
                        <div class="card-title-group">
                            <i class="bi bi-pie-chart-fill icon text-primary"></i>
                            <h3>Share de Status Mensal</h3>
                        </div>
                        <div class="card-filters row-flex gap-2">
                            <div class="select-wrapper default-border minified">
                                <select v-model="filters.compareServices.ano" @change="loadDataCompareServices()" class="form-select">
                                    <option v-for="a in anosDisponiveis" :key="a" :value="a">{{ a }}</option>
                                </select>
                                <i class="bi bi-chevron-down select-icon"></i>
                            </div>
                            <div class="select-wrapper default-border minified">
                                <select v-model="filters.compareServices.mes" @change="loadDataCompareServices()" class="form-select">
                                    <option v-for="(nome, index) in mesesDisponiveis" :key="index" :value="index + 1">{{ nome }}</option>
                                </select>
                                <i class="bi bi-chevron-down select-icon"></i>
                            </div>
                        </div>
                    </div>
                    <div class="card-body-section">
                        <div v-if="loadStates.compareServices" class="v2-card-loader">
                            <div class="spinner"></div><span>Carregando módulo...</span>
                        </div>
                        <BaseChart v-else-if="chartOptionsCompareServices" :option="chartOptionsCompareServices" />
                        <div v-else class="chart-empty-state">
                            <i class="bi bi-pie-chart empty-state-icon"></i>
                            <p class="empty-state-text">Nenhum dado registrado para o período selecionado</p>
                        </div>
                    </div>
                </div>

                <!-- 8. Compare Employee -->
                <div v-if="hasFeature('GRAPH_COMPARE_EMPLOYEES')" class="dashboard-card full-span">
                    <div class="card-header-bar">
                        <div class="card-title-group">
                            <i class="bi bi-person-lines-fill icon text-primary"></i>
                            <h3>Prestador por Categoria Segmentada</h3>
                        </div>
                        <div class="card-filters row-flex gap-2">
                            <div class="select-wrapper default-border minified">
                                <select v-model="filters.compareEmployee.mes" @change="loadDataCompareEmployee()" class="form-select">
                                    <option v-for="(nome, index) in mesesDisponiveis" :key="index" :value="index + 1">{{ nome }}</option>
                                </select>
                                <i class="bi bi-chevron-down select-icon"></i>
                            </div>
                            <div class="select-wrapper default-border minified">
                                <select v-model="filters.compareEmployee.tipoServico" @change="loadDataCompareEmployee()" class="form-select">
                                    <option v-for="(nome, index) in tiposServicos" :key="index" :value="nome">{{ nome }}</option>
                                </select>
                                <i class="bi bi-chevron-down select-icon"></i>
                            </div>
                        </div>
                    </div>
                    <div class="card-body-section">
                        <div v-if="loadStates.compareEmployee" class="v2-card-loader">
                            <div class="spinner"></div><span>Carregando módulo...</span>
                        </div>
                        <BaseChart v-else-if="chartOptionsCompareEmployee" :option="chartOptionsCompareEmployee" />
                        <div v-else class="chart-empty-state">
                            <i class="bi bi-person-lines-fill empty-state-icon"></i>
                            <p class="empty-state-text">Nenhum prestador com atendimentos registrados nesta categoria</p>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Top Motivos (Ranking) -->
            <div v-if="hasFeature('GRAPH_MOTIVOS')" class="dashboard-motivos-section mt-5">
                
                <h3 class="section-heading mb-4 text-center">
                    <i class="bi bi-trophy-fill text-warning me-2"></i> Rank de Interações ({{ periodoTopMotivo?.start || '...' }} a {{ periodoTopMotivo?.end || '...' }})
                </h3>

                <!-- Top Motivos Loader -->
                <div v-if="loadStates.motivos" class="v2-card-loader" style="min-height: 200px;">
                    <div class="spinner border-spinner"></div>
                    <span>Minerando dados de rank (Isso pode levar alguns segundos)...</span>
                </div>

                <div v-else class="v2-dashboard-grid thirds">
                    <div v-for="tipo in tipos" :key="tipo" class="dashboard-card list-card">
                        <div class="card-header-bar">
                            <div class="card-title-group">
                                <h3 class="text-capitalize">Top 10 - {{ tipo }}</h3>
                            </div>
                        </div>
                        <div class="card-body-section">
                            <template v-if="resultados[tipo] && resultados[tipo].length > 0">
                                <v-chart v-if="chartOptions[tipo]" :option="chartOptions[tipo]" class="motivos-chart mb-3" />
                                
                                <ul class="list-group modern-list">
                                    <li v-for="item in resultados[tipo] || []" :key="item.mensagem" class="modern-list-item">
                                        <div class="list-content">
                                            <strong>{{ item.mensagem }}</strong>
                                            <div class="text-muted small">Qtd: {{ item.quantidade }}</div>
                                        </div>
                                        <button class="circle-btn small-btn" @click="copiarIds(item.numeroServicos)" title="Copiar IDs">
                                            <i class="bi bi-copy"></i>
                                        </button>
                                    </li>
                                </ul>
                            </template>
                            <div v-else class="chart-empty-state">
                                <i class="bi bi-chat-square-dots empty-state-icon"></i>
                                <p class="empty-state-text">Nenhum motivo registrado no período</p>
                            </div>
                        </div>
                    </div>
                </div>

            </div>
            
        </div>

        <div class="toast-container position-fixed bottom-0 end-0 p-3" style="z-index: 1055;">
            <div id="liveToast" class="toast" role="alert" aria-live="assertive" aria-atomic="true">
                <div class="toast-header">
                    <i class="bi bi-info-circle-fill text-primary me-2"></i>
                    <strong class="me-auto">Atualização do Gráfico</strong>
                    <button type="button" class="btn-close" data-bs-dismiss="toast" aria-label="Close"></button>
                </div>
                <div class="toast-body" id="toastBody"></div>
            </div>
        </div>

    </LayoutAutenticado>
</template>

<script setup>
import { ref, onMounted, reactive, computed } from 'vue';
import axios from '@/axios.js';
import LayoutAutenticado from '../components/layouts/LayoutAutenticado.vue';
import { showToast } from '@/eventBus.js';
import BaseChart from '../components/BaseChartComponent.vue';

let companyId = ref(null);

const chartOptionsReceivers = ref(null);
const chartOptionsPagment = ref(null);
const chartOptionsServices = ref(null);
const chartOptionsEmployee = ref(null);
const chartOptionsCompare = ref(null);
const chartOptionsConsultaService = ref(null);
const chartOptionsCompareServices = ref(null);
const chartOptionsCompareEmployee = ref(null);

// Controlled Loading States (Independent rendering)
const loadStates = reactive({
    receivers: true,
    pagment: true,
    services: true,
    employees: true,
    compare: true,
    consultaService: true,
    compareServices: true,
    compareEmployee: true,
    motivos: true
});

const filters = reactive({
    receivers: { ano: new Date().getFullYear() },
    pagment: { ano: new Date().getFullYear() },
    services: { ano: new Date().getFullYear() },
    employees: { ano: new Date().getFullYear(), mes: new Date().getMonth() + 1 },
    compare: { mes: new Date().getMonth() + 1 },
    consultaService: { ano: new Date().getFullYear(), tipoServico: 'Visita' },
    compareServices: { ano: new Date().getFullYear(), mes: new Date().getMonth() + 1 },
    compareEmployee: { mes: new Date().getMonth() + 1, tipoServico: 'Visita' }
});

const mesesDisponiveis = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
];
const tiposServicos = ref([]);
const anosDisponiveis = Array.from({ length: 4 }, (_, i) => new Date().getFullYear() - i);
const periodoTopMotivo = ref();

const tipos = ref(['visita', 'garantia', 'cancelado'])
const resultados = ref({})
const chartOptions = ref({})

const companyFeatures = ref([]);

onMounted(async () => {
    companyId.value = localStorage.getItem("idCompany");

    try {
        const featuresResponse = await axios.get(`/api/dashboard/features/${companyId.value}`);
        companyFeatures.value = featuresResponse.data.enabledFeatures;
    } catch (error) {
        showToast(error?.response?.data?.message || 'Erro ao mapear ACL do dashboard.');
        return; 
    }

    if (hasAnyFeature.value) {
        loadAllDashboardData();
    }
});

function hasFeature(featureKey) {
    return companyFeatures.value.includes(featureKey);
}

const hasAnyFeature = computed(() => companyFeatures.value.length > 0);

function loadAllDashboardData() {
    loadTiposServicos();

    const hasPagment = hasFeature("GRAPH_PAGMENT");
    const hasServices = hasFeature("GRAPH_COMPARE_SERVICE");

    // Init modules concurrently but strictly independent
    if (hasPagment && hasServices) {
        loadInitialPagmentAndServices();
    } else {
        if (hasPagment) loadDataPagment();
        if (hasServices) loadDataServices();
    }

    if (hasFeature("GRAPH_RECEIVERS")) loadDataReceivers();
    if (hasFeature("GRAPH_EMPLOYEES")) loadDataEmployees();
    if (hasFeature("GRAPH_COMPARE_COMPANYS")) loadDataCompare();
    if (hasFeature("GRAPH_CONSULT_SERVICES")) loadDataConsultaService();
    if (hasFeature("GRAPH_INFORMATION_SERVICES")) loadDataCompareServices();
    if (hasFeature("GRAPH_COMPARE_EMPLOYEES")) loadDataCompareEmployee();
    if (hasFeature("GRAPH_MOTIVOS")) loadDataTopMotivos();
}

function loadTiposServicos() {
    axios.get(`/api/equipments/all/${companyId.value}`)
        .then(response => { tiposServicos.value = response.data.data.map(equip => equip.name); })
        .catch(() => {});
}

// ----------------------------------------------------------------------
// Loaders Locais (Desacoplados)
// ----------------------------------------------------------------------

function loadInitialPagmentAndServices() {
    loadStates.pagment = true;
    loadStates.services = true;

    axios.get(`/api/dashboard/pagment/${companyId.value}`, { params: { ano: filters.pagment.ano } })
    .then(response => {
        const data = response.data?.data || [];
        chartOptionsPagment.value = getPagmentChartOptions(data);
        chartOptionsServices.value = getServicesChartOptions(data);
    })
    .catch(() => { 
        showToast("Falha ao recuperar pagamentos");
        chartOptionsPagment.value = null;
        chartOptionsServices.value = null;
    })
    .finally(() => {
        loadStates.pagment = false;
        loadStates.services = false;
    });
}

function getPagmentChartOptions(data) {
    if (!data || data.length === 0 || !data.some(item => Number(item.total) > 0)) {
        return null;
    }
    return {
        tooltip: { trigger: 'axis', valueFormatter: (total) => `R$ ${total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` },
        xAxis: { type: 'category', data: data.map(item => item.month), axisLabel: { interval: 0, rotate: 30 } },
        yAxis: { type: 'value' },
        series: [{
            name: 'Pagar', type: 'bar', showBackground: true,
            backgroundStyle: { color: 'rgba(239, 68, 68, 0.05)' },
            data: data.map(item => item.total),
            itemStyle: { color: '#EF4444', borderRadius: [4, 4, 0, 0] },
            label: { show: true, position: 'top', formatter: (params) => `R$ ${params.value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` },
            emphasis: { itemStyle: { shadowBlur: 10, shadowColor: 'rgba(0, 0, 0, 0.1)' } }
        }],
        grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true }
    };
}

function getServicesChartOptions(data) {
    if (!data || data.length === 0 || !data.some(item => Number(item.quantity) > 0)) {
        return null;
    }
    return {
        tooltip: { trigger: 'axis' },
        xAxis: { type: 'category', data: data.map(item => item.month), axisLabel: { interval: 0, rotate: 30 } },
        yAxis: { type: 'value' },
        series: [{
            name: 'Volume', type: 'bar', showBackground: true,
            backgroundStyle: { color: 'rgba(245, 158, 11, 0.05)' },
            data: data.map(item => item.quantity),
            itemStyle: { color: '#F59E0B', borderRadius: [4, 4, 0, 0] },
            label: { show: true, position: 'top', fontSize: 11, fontWeight: 'bold' },
            emphasis: { itemStyle: { shadowBlur: 10, shadowColor: 'rgba(0, 0, 0, 0.1)' } }
        }],
        grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true }
    };
}

function loadDataPagment() {
    loadStates.pagment = true;
    axios.get(`/api/dashboard/pagment/${companyId.value}`, { params: { ano: filters.pagment.ano } })
    .then(response => { 
        chartOptionsPagment.value = getPagmentChartOptions(response.data?.data || []); 
    })
    .catch(() => {
        chartOptionsPagment.value = null;
    })
    .finally(() => { loadStates.pagment = false; });
}

function loadDataServices() {
    loadStates.services = true;
    axios.get(`/api/dashboard/pagment/${companyId.value}`, { params: { ano: filters.services.ano } })
    .then(response => { 
        chartOptionsServices.value = getServicesChartOptions(response.data?.data || []); 
    })
    .catch(() => {
        chartOptionsServices.value = null;
    })
    .finally(() => { loadStates.services = false; });
}

function loadDataReceivers() {
    loadStates.receivers = true;
    axios.get(`/api/dashboard/receivers/${companyId.value}`, { params: { ano: filters.receivers.ano } })
    .then(response => {
        const rawData = response.data?.data || [];
        const hasData = rawData.some(item => Number(item.total) > 0);

        if (!hasData || rawData.length === 0) {
            chartOptionsReceivers.value = null;
            return;
        }

        chartOptionsReceivers.value = {
            tooltip: { trigger: 'axis', valueFormatter: (total) => `R$ ${total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` },
            xAxis: { type: 'category', data: rawData.map(item => item.month), axisLabel: { interval: 0, rotate: 30 } },
            yAxis: { type: 'value' },
            series: [{
                name: 'Recebimentos', type: 'bar', data: rawData.map(item => item.total),
                showBackground: true, backgroundStyle: { color: 'rgba(16, 185, 129, 0.05)' },
                itemStyle: { color: '#10B981', borderRadius: [4, 4, 0, 0] },
                label: { show: true, position: 'top', formatter: (params) => `R$ ${params.value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` }
            }],
            grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true }
        };
    })
    .catch(() => {
        chartOptionsReceivers.value = null;
    })
    .finally(() => { loadStates.receivers = false; });
}

function loadDataEmployees() {
    loadStates.employees = true;
    axios.get(`/api/dashboard/employees/${companyId.value}`, { params: { ano: filters.employees.ano, mes: filters.employees.mes } })
    .then(response => {
        const rawData = response.data?.data || [];
        // Segunda camada de proteção: filtra apenas funcionários válidos com quantidade > 0
        const data = rawData.filter(item => item && item.employee && item.employee.name && Number(item.quantity) > 0);

        if (data.length === 0) {
            chartOptionsEmployee.value = null;
            return;
        }

        chartOptionsEmployee.value = {
            tooltip: { trigger: 'axis' },
            xAxis: { 
                type: 'category', 
                data: data.map(item => item.employee.name), 
                axisLabel: { interval: 0, rotate: 10 } 
            },
            yAxis: { type: 'value' },
            series: [{
                name: 'Serviços', 
                type: 'bar', 
                data: data.map(item => item.quantity),
                colorBy: data.map(item => item.employee.name), 
                showBackground: true,
                itemStyle: {
                    color: (params) => {
                        const colorList = ['#DF523A', '#3B82F6', '#10B981', '#F59E0B', '#8B5CF6'];
                        return colorList[params.dataIndex % colorList.length];
                    },
                    borderRadius: [4, 4, 0, 0]
                },
                backgroundStyle: { color: 'rgba(0,0,0, 0.02)' },
                label: { show: true, position: 'top', fontSize: 11, fontWeight: 'bold' }
            }],
            grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true }
        };
    })
    .catch(() => {
        chartOptionsEmployee.value = null;
    })
    .finally(() => { loadStates.employees = false; });
}

function loadDataCompare() {
    loadStates.compare = true;
    axios.get(`/api/dashboard/compare/${companyId.value}`, { params: { mes: filters.compare.mes } })
    .then(response => {
        const rawData = response.data?.data || [];
        const data = rawData.filter(item => item && item.name && Number(item.quantity) > 0);

        if (data.length === 0) {
            chartOptionsCompare.value = null;
            return;
        }

        chartOptionsCompare.value = {
            tooltip: { trigger: 'axis' },
            xAxis: { type: 'category', data: data.map(item => item.name), axisLabel: { interval: 0, rotate: 30 } },
            yAxis: { type: 'value' },
            series: [{
                name: 'Ranking Base', type: 'bar', data: data.map(item => item.quantity),
                colorBy: data.map(item => item.name), showBackground: true,
                backgroundStyle: { color: 'rgba(0,0,0, 0.02)' },
                itemStyle: { color: '#3B82F6', borderRadius: [4, 4, 0, 0] },
                label: { show: true, position: 'top', fontSize: 11, fontWeight: 'bold' }
            }],
            grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true }
        };
    })
    .catch(() => {
        chartOptionsCompare.value = null;
    })
    .finally(() => { loadStates.compare = false; });
}

function loadDataConsultaService() {
    loadStates.consultaService = true;
    axios.get(`/api/dashboard/information-services/${companyId.value}`, { params: { ano: filters.consultaService.ano, tipoServico: filters.consultaService.tipoServico } })
    .then(response => {
        const rawData = response.data?.data || [];
        const hasData = rawData.some(item => Number(item.count) > 0);

        if (!hasData || rawData.length === 0) {
            chartOptionsConsultaService.value = null;
            return;
        }

        chartOptionsConsultaService.value = {
            tooltip: { trigger: 'axis' },
            xAxis: { type: 'category', data: rawData.map(item => item.month), axisLabel: { interval: 0, rotate: 30 } },
            yAxis: { type: 'value' },
            series: [{
                name: 'Quantidade', type: 'bar', data: rawData.map(item => item.count),
                itemStyle: { color: '#8B5CF6', borderRadius: [4, 4, 0, 0] }, showBackground: true,
                backgroundStyle: { color: 'rgba(139, 92, 246, 0.05)' },
                label: { show: true, position: 'top', fontSize: 11, fontWeight: 'bold' }
            }],
            grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true }
        };
    })
    .catch(() => {
        chartOptionsConsultaService.value = null;
    })
    .finally(() => { loadStates.consultaService = false; });
}

function loadDataCompareServices() {
    loadStates.compareServices = true;
    axios.get(`/api/dashboard/compare-services/${companyId.value}`, { params: { ano: filters.compareServices.ano, mes: filters.compareServices.mes } })
    .then(response => {
        const s = response.data?.data || {};
        const total = (Number(s.cancelad) || 0) + (Number(s.warranty) || 0) + (Number(s.completed) || 0) + (Number(s.visit) || 0);

        if (total === 0) {
            chartOptionsCompareServices.value = null;
            return;
        }

        const data = [
            { value: s.cancelad, name: 'Cancelados', itemStyle: { color: '#EF4444' } },
            { value: s.warranty, name: 'Garantia', itemStyle: { color: '#3B82F6' } },
            { value: s.completed, name: 'Concluídos', itemStyle: { color: '#10B981' } },
            { value: s.visit, name: 'Visita', itemStyle: { color: '#F59E0B' } }
        ].filter(item => Number(item.value) > 0);

        chartOptionsCompareServices.value = {
            tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
            legend: { orient: 'vertical', left: 'left' },
            series: [{
                name: 'Rate', type: 'pie', radius: ['35%', '65%'],
                avoidLabelOverlap: true,
                itemStyle: { borderRadius: 6, borderColor: '#FFF', borderWidth: 2 },
                label: { show: true, formatter: '{b}\n{c} ({d}%)', fontSize: 12, fontWeight: 'bold' },
                labelLine: { show: true, length: 15, length2: 10 },
                data
            }]
        };
    })
    .catch(() => {
        chartOptionsCompareServices.value = null;
    })
    .finally(() => { loadStates.compareServices = false; });
}

function loadDataCompareEmployee() {
    loadStates.compareEmployee = true;
    axios.get(`/api/dashboard/compare-employee/${companyId.value}`, { params: { tipoServico: filters.compareEmployee.tipoServico, mes: filters.compareEmployee.mes } })
    .then(response => {
        const rawData = response.data?.data || [];
        // Segunda camada de proteção: filtra apenas prestadores válidos com quantidade > 0
        const data = rawData.filter(item => item && item.employee && item.employee.name && Number(item.count) > 0);

        if (data.length === 0) {
            chartOptionsCompareEmployee.value = null;
            return;
        }

        chartOptionsCompareEmployee.value = {
            tooltip: { trigger: 'axis' },
            xAxis: { 
                type: 'category', 
                data: data.map(item => item.employee.name), 
                axisLabel: { interval: 0, rotate: 10 } 
            },
            yAxis: { type: 'value' },
            series: [{
                name: 'Quantidade', 
                type: 'bar', 
                data: data.map(item => item.count),
                itemStyle: { color: '#DF523A', borderRadius: [4, 4, 0, 0] },
                showBackground: true, 
                backgroundStyle: { color: 'rgba(223, 82, 58, 0.05)' },
                label: { show: true, position: 'top', fontSize: 11, fontWeight: 'bold' }
            }],
            grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true }
        };
    })
    .catch(() => {
        chartOptionsCompareEmployee.value = null;
    })
    .finally(() => { loadStates.compareEmployee = false; });
}

async function fetchWithRetry(url, options = {}, retries = 2, backoff = 300) {
    try {
        return await axios.get(url, options);
    } catch (err) {
        if (retries > 0) {
            await new Promise(r => setTimeout(r, backoff));
            return fetchWithRetry(url, options, retries - 1, backoff * 2);
        }
        throw err;
    }
}

async function loadDataTopMotivos() {
    loadStates.motivos = true;
    const promises = tipos.value.map(async (tipo) => {
        try {
            const r = await fetchWithRetry(`/api/dashboard/motivos/${companyId.value}`, { params: { ano: new Date().getFullYear(), tipoServico: tipo }, timeout: 80000 });
            const raw = r.data;
            periodoTopMotivo.value = raw?.data?.period || '';
            const rawItems = Array.isArray(raw) ? raw : raw?.data?.motivos || [];
            const data = rawItems.filter(i => i && Number(i.quantidade) > 0);

            resultados.value[tipo] = data;

            if (data.length === 0) {
                chartOptions.value[tipo] = null;
            } else {
                chartOptions.value[tipo] = {
                    tooltip: { trigger: "item" },
                    grid: { left: "0%", right: "10%", bottom: "0%", containLabel: true },
                    xAxis: { type: "value", boundaryGap: [0, 0.01] },
                    yAxis: { type: "category", data: data.map(i => i.mensagem ?? "--"), axisLabel: { show: false } },
                    series: [{ type: "bar", data: data.map(i => i.quantidade ?? 0), itemStyle: { color: "#3B82F6", borderRadius: [0, 4, 4, 0] }, label: { show: true, position: 'right', fontSize: 11, fontWeight: 'bold' } }]
                };
            }
        } catch {
            resultados.value[tipo] = [];
            chartOptions.value[tipo] = null;
        }
    });

    Promise.all(promises).finally(() => { loadStates.motivos = false; });
}

function copiarIds(ids) {
    if (!ids || ids.length === 0) return;
    navigator.clipboard.writeText(ids.join(', '))
    showToast('IDs copiados para o seu Clipboard!')
}
</script>

<style scoped>
/* Page Skeleton */
.dashboard-v2 {
  width: 100%;
}

.page-header {
  margin-bottom: 1.5rem;
}
.page-title {
  font-size: 1.75rem;
  font-weight: 800;
  color: #1E293B;
  margin-bottom: 0.25rem;
}
.page-subtitle {
  font-size: 0.95rem;
  color: #64748B;
  margin: 0;
}

/* Empty restrictions */
.no-graphs {
  text-align: center;
  padding: 5rem 2rem;
  background: #FFF;
  border-radius: 16px;
  border: 1px dashed #CBD5E1;
  color: #64748B;
  margin-top: 2rem;
}
.empty-icon {
  font-size: 3.5rem;
  color: #94A3B8;
  margin-bottom: 1rem;
  opacity: 0.5;
}
.no-graphs h2 {
  color: #0F172A;
  font-weight: 700;
}

/* Base Responsive Grid layout */
.v2-dashboard-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(450px, 1fr));
  gap: 1.5rem;
}
.v2-dashboard-grid.thirds {
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
}

@media (max-width: 900px) {
  .v2-dashboard-grid { grid-template-columns: 1fr; }
}

.full-span {
  grid-column: 1 / -1;
}

/* SaaS Chart Card */
.dashboard-card {
  background: #FFFFFF;
  border: 1px solid #E2E8F0;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 4px 6px rgba(0,0,0,0.02);
  transition: box-shadow 0.2s ease;
  min-height: 400px;
}
.dashboard-card:hover {
  box-shadow: 0 8px 16px rgba(0,0,0,0.05);
}

.card-header-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #F1F5F9;
  background: #FFF;
}

.card-title-group {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.card-title-group h3 {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
  color: #0F172A;
}

.card-title-group .icon {
  font-size: 1.2rem;
}

.text-success { color: #10B981; }
.text-danger { color: #EF4444; }
.text-warning { color: #F59E0B; }
.text-primary { color: #3B82F6; }

/* Injected select styling */
.row-flex {
  display: flex;
  align-items: center;
  gap: 10px;
}

.select-wrapper {
  position: relative;
}
.select-wrapper.default-border.minified {
  background: #F8FAFC;
  border: 1px solid #E2E8F0;
  border-radius: 8px;
  padding: 0.35rem 0.85rem;
}
.select-wrapper.default-border.minified:focus-within {
  border-color: #DF523A;
  background-color: #FFF;
}

.form-select {
  width: auto;
  min-width: 90px;
  appearance: none;
  background: transparent;
  border: none;
  font-family: inherit;
  font-weight: 600;
  color: #334155;
  font-size: 0.85rem;
  outline: none;
  cursor: pointer;
  padding-right: 18px;
}

.select-icon {
  position: absolute;
  right: 0.6rem;
  top: 50%;
  transform: translateY(-50%);
  color: #94A3B8;
  pointer-events: none;
  font-size: 0.8rem;
}

/* Card Body (Chart Wrapper) */
.card-body-section {
  flex: 1;
  padding: 1rem;
  position: relative;
  display: flex;
  flex-direction: column;
}

/* Isolated Local Card Loader (Elimina o congelamento do DOM) */
.v2-card-loader {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  flex: 1;
  min-height: 250px;
  color: #64748B;
  gap: 1rem;
  font-weight: 600;
  animation: pulseOpacity 1.5s infinite;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #E2E8F0;
  border-top-color: #DF523A;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}
.border-spinner {
  border-top-color: #3B82F6;
}

@keyframes spin { 
  to { transform: rotate(360deg); } 
}
@keyframes pulseOpacity {
  0% { opacity: 0.6; }
  50% { opacity: 1; }
  100% { opacity: 0.6; }
}

/* Motivos & List Grid */
.section-heading {
  font-size: 1.25rem;
  font-weight: 800;
  color: #1E293B;
}

.motivos-chart {
  height: 120px;
  width: 100%;
}

.modern-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.modern-list-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #F8FAFC;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  border: 1px solid #E2E8F0;
  transition: border-color 0.2s;
}
.modern-list-item:hover {
  border-color: #CBD5E1;
}

.list-content strong {
  color: #0F172A;
  font-size: 0.9rem;
}
.list-content .text-muted {
  font-size: 0.8rem;
  font-weight: 500;
}

.circle-btn.small-btn {
  width: 32px;
  height: 32px;
  background: #E2E8F0;
  border: none;
  border-radius: 50%;
  color: #475569;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}
.circle-btn.small-btn:hover {
  background: #DF523A;
  color: #FFF;
}

/* Empty state for charts */
.chart-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  flex: 1;
  min-height: 250px;
  color: #94A3B8;
  gap: 0.75rem;
  text-align: center;
  padding: 2rem 1rem;
}

.empty-state-icon {
  font-size: 2.2rem;
  color: #CBD5E1;
}

.empty-state-text {
  font-size: 0.9rem;
  font-weight: 500;
  color: #64748B;
  margin: 0;
  max-width: 280px;
  line-height: 1.4;
}

/* Animations base */
@keyframes fadeInUp {
  from { opacity: 0; transform: translateY(15px); }
  to { opacity: 1; transform: translateY(0); }
}
.fade-in-up {
  animation: fadeInUp 0.5s ease forwards;
}
</style>